import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  CollapseBtnWrapper,
  MatterDetailsWrapper,
  ResizerButton,
  SideBarContainer,
  SideBarWrapper,
  MainContentWrapper,
  AIPaneWrapper,
  AIPaneResizerButton,
  ShellSkeletonOverlay,
} from "./MatterDetailsStyled";
import { useLocation, useNavigate } from "react-router-dom";
import TreeComponent from "../../components/TreeComponent/TreeComponent";
import TabView from "../../components/TabView/TabView";
import AIPane from "../../components/AIPane";
import MatterDetailsShellSkeleton from "./MatterDetailsShellSkeleton";
import {
  AI_PANE_MODE_MATTER,
  AI_PANE_MODE_MATTER_CHAT_ONLY,
  getActiveDocumentIdFromPath,
  getMatterIdFromPath,
  isAIPaneOpen,
} from "../../utils/aiPaneUrl";
import { isChatOnlyCaseType } from "../../utils/matterFields";
import { claimBriefReaderMatterWindowName } from "../../utils/briefReaderWindowBridge";
import { useDispatch, useSelector } from "react-redux";
import { convertData } from "../../utils/RestructureToDateTree";
import { cleanupScrollState } from "../../utils/scrollCleanup";
import CollapseIcon from "../../assets/images/CollapseIcon.svg";
import { Button, Popover } from "antd";
import {
  setSelectedDetails,
  setOpenDocs,
  setSelectedKeys,
  resetMatterDetails,
  setDocListingPage,
  postLastPageViewed,
} from "../../slice/MatterDetailsSlice";
import { updateShowActions } from "../../slice/PDFEditSlice";
import { setCurrentContext } from "../../slice/WebsocketSlice";
import { clearDocumentAIState } from "../../slice/AISlice";
import { useUploadNotification } from "../../context/UploadNotificationContext";

const MatterDetails = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  // Matter id from the route (`/matter/:id/...`). Derived from the pathname
  // rather than useParams() so this component doesn't require a Router context
  // its existing tests don't provide (they mock useLocation, not a Router).
  const matterIdParam = location.pathname.split("/")[2] || "";
  const { matterDetailsTree, matterDetailsStatus } = useSelector((state) => state.matterDetails);
  const hasNavigatedRef = useRef(false);
  const { showNotification: showUploadNotification } = useUploadNotification();

  // Sidebar visibility from localStorage or default true
  const [showSideBar, setShowSideBar] = useState(
    localStorage.getItem("sidebar") === "true" || false
  );

  // Sidebar width with default 220px (compact; user can resize wider).
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem("sidebarWidth");
    return savedWidth ? Number(savedWidth) : 220;
  });

  const [isResizing, setIsResizing] = useState(false);
  const [aiPaneWidth, setAiPaneWidth] = useState(() => {
    const savedWidth = localStorage.getItem("aiPaneWidth");
    return savedWidth ? Number(savedWidth) : 500;
  });
  const [isAIPaneResizing, setIsAIPaneResizing] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1920
  );

  // Read AI-pane state from the URL up here so getAvailableSidebarWidth
  // (declared below) can reference it without a temporal-dead-zone issue.
  // CRA's Babel output transpiles `const` to `var`, so referencing this
  // below the declaration looks fine in dev but silently evaluates to
  // `undefined` — meaning panelWidth was always 0 in hover-expand.
  //
  // `isAIPaneOpen` covers both the new `?ai=…` query param and the
  // legacy `?chatbox=true` flag (treated as `?ai=chat` on read) so any
  // bookmarks or in-flight links that still ride the legacy param keep
  // working without a flash of mis-sized layout. AIPane reads the active
  // tab from URL itself, so we don't track that locally.
  const isAIOpen = isAIPaneOpen(location.search);
  const matterId = getMatterIdFromPath(location.pathname);
  const activeDocumentId = getActiveDocumentIdFromPath(location.pathname);

  // Captures the cursor X and sidebar width at mouse-down so resize is
  // delta-based. Without this, the first mousemove event snaps sidebarWidth
  // to e.clientX, which is ~72px larger than the actual sidebar (primary
  // sidebar offset) — producing a visible jump on every drag start.
  const dragStartRef = useRef({ startX: 0, startWidth: 0 });

  // Hover-to-expand: temporarily widen a compact sidebar while hovering so long
  // folder/file names can be read without permanently giving up horizontal space.
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const SIDEBAR_MIN_WIDTH = 200;
  const SIDEBAR_MAX_WIDTH = 500;
  const HOVER_EXPAND_CAP = 500;
  const AI_PANE_MIN_WIDTH = 420;
  const AI_PANE_MAX_WIDTH = 820;

  // Available sidebar width given the current viewport and whether the AI
  // panel is open. Shared by the drag clamp, auto-adjust effect, and
  // hover-expand target so all three agree on "as wide as we can afford."
  const getAvailableSidebarWidth = useCallback((cap = SIDEBAR_MAX_WIDTH) => {
    const aiPanelWidth = isAIOpen ? aiPaneWidth + 8 : 0; // panel + 8px margin
    // Reserve primary sidebar (72) + outer gaps (16+16) + min content (400).
    return Math.min(cap, viewportWidth - 72 - 16 - aiPanelWidth - 400 - 16);
  }, [aiPaneWidth, isAIOpen, viewportWidth]);

  // Hover-expand target: as wide as we can afford, up to HOVER_EXPAND_CAP.
  // Only fires when it would actually make the sidebar wider — otherwise
  // there's no point in hiding/showing the hover state.
  const hoverExpandedWidth = Math.max(
    sidebarWidth,
    getAvailableSidebarWidth(HOVER_EXPAND_CAP)
  );
  const shouldHoverExpand =
    isSidebarHovered && !isResizing && hoverExpandedWidth > sidebarWidth;
  const effectiveSidebarWidth = shouldHoverExpand
    ? hoverExpandedWidth
    : sidebarWidth;
  const [breadcrumb, setBreadcrumb] = useState([{ id: 0, name: "Matters" }]);
  const [uniqueId, setUniqueId] = useState("");
  const [drawerError, setdrawerError] = useState([]);
  const currentPage = useSelector((state) => state.matterDetails.docListingPage);
  const setCurrentPage = (page) => dispatch(setDocListingPage(page));
  const checkTabLimitRef = useRef(null); // Ref to store checkTabLimit function from TabView
  const cleanupDocumentRef = useRef(null); // Ref to store cleanupDocument function from TabView
  const openTabRef = useRef(null); // Ref to store openTab function from TabView
  const setActiveTabRef = useRef(null); // Ref to store setActiveTab function from TabView
  const clearFactSelectionRef = useRef(null); // Ref for ChatPane to clear fact selection when document tab is closed
  const aiPaneDragStartRef = useRef({ startX: 0, startWidth: 500 });
  const aiPaneWidthRef = useRef(aiPaneWidth);
  const autoHidingSidebarRef = useRef(isAIOpen && showSideBar);
  const wasAIOpenRef = useRef(false);


  const newTreeData = useMemo(() => {
    return convertData([matterDetailsTree]);
  }, [matterDetailsTree]);

  // Mouse move/up handlers for resizing sidebar
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      // Delta-based: apply the cursor movement since mousedown to the width
      // at mousedown. This way the resizer tracks the cursor exactly and
      // the sidebar doesn't jump when the drag begins — regardless of
      // where the sidebar starts on screen (primary sidebar offset, etc).
      const { startX, startWidth } = dragStartRef.current;
      const maxAllowedWidth = getAvailableSidebarWidth();
      const target = startWidth + (e.clientX - startX);
      const newWidth = Math.min(
        Math.max(target, SIDEBAR_MIN_WIDTH),
        Math.max(SIDEBAR_MIN_WIDTH, maxAllowedWidth)
      );
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        localStorage.setItem("sidebarWidth", sidebarWidth); // Save width
        // A drag is an explicit sizing action. Clear hover-expand so we don't
        // immediately re-expand the sidebar back to HOVER_EXPAND_WIDTH just
        // because the cursor is still over it — hover-expand will re-arm the
        // next time the user leaves and re-enters the sidebar.
        setIsSidebarHovered(false);
      }
    };

    globalThis.addEventListener("mousemove", handleMouseMove);
    globalThis.addEventListener("mouseup", handleMouseUp);

    return () => {
      globalThis.removeEventListener("mousemove", handleMouseMove);
      globalThis.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, sidebarWidth, isAIOpen, getAvailableSidebarWidth]);

  const getAvailableAIPaneWidth = useCallback((cap = AI_PANE_MAX_WIDTH) => {
    const sidebarSlot = showSideBar ? sidebarWidth + 8 : 0;
    // Reserve primary sidebar (72) + outer gaps (16+16) + min document viewer (480).
    return Math.min(cap, viewportWidth - 72 - 16 - sidebarSlot - 480 - 16);
  }, [showSideBar, sidebarWidth, viewportWidth]);

  const clampAIPaneWidth = useCallback((targetWidth) => {
    const maxAllowedWidth = Math.max(0, getAvailableAIPaneWidth());
    const minAllowedWidth = Math.min(AI_PANE_MIN_WIDTH, maxAllowedWidth);
    return Math.min(Math.max(targetWidth, minAllowedWidth), maxAllowedWidth);
  }, [getAvailableAIPaneWidth]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isAIPaneResizing) return;
      const { startX, startWidth } = aiPaneDragStartRef.current;
      const target = startWidth - (e.clientX - startX);
      const newWidth = clampAIPaneWidth(target);
      aiPaneWidthRef.current = newWidth;
      setAiPaneWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isAIPaneResizing) {
        setIsAIPaneResizing(false);
        localStorage.setItem("aiPaneWidth", aiPaneWidthRef.current);
      }
    };

    globalThis.addEventListener("mousemove", handleMouseMove);
    globalThis.addEventListener("mouseup", handleMouseUp);

    return () => {
      globalThis.removeEventListener("mousemove", handleMouseMove);
      globalThis.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isAIPaneResizing, clampAIPaneWidth]);

  useEffect(() => {
    aiPaneWidthRef.current = aiPaneWidth;
  }, [aiPaneWidth]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(typeof window !== "undefined" ? window.innerWidth : 1920);
    };
    globalThis.addEventListener("resize", handleResize);
    return () => globalThis.removeEventListener("resize", handleResize);
  }, []);

  // Automatically adjust sidebar width when the AI panel opens/closes to prevent overflow
  useEffect(() => {
    if (!isResizing) {
      // Use the same cap as manual drag + hover-expand so the three
      // interactions never disagree on "largest allowed width."
      const maxAllowedWidth = getAvailableSidebarWidth();
      if (sidebarWidth > maxAllowedWidth) {
        const newWidth = Math.max(SIDEBAR_MIN_WIDTH, maxAllowedWidth);
        setSidebarWidth(newWidth);
        localStorage.setItem("sidebarWidth", newWidth);
      }
    }
  }, [isAIOpen, isResizing, sidebarWidth, getAvailableSidebarWidth]); // Adjust when AI panel state changes

  useEffect(() => {
    if (!isAIOpen || isAIPaneResizing) return;
    if (autoHidingSidebarRef.current) return;
    const nextWidth = clampAIPaneWidth(aiPaneWidth);
    if (aiPaneWidth !== nextWidth) {
      setAiPaneWidth(nextWidth);
      aiPaneWidthRef.current = nextWidth;
      localStorage.setItem("aiPaneWidth", nextWidth);
    }
  }, [aiPaneWidth, isAIOpen, isAIPaneResizing, clampAIPaneWidth]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedDetails({}));
      dispatch(setOpenDocs([]));
      dispatch(setSelectedKeys([]));
      dispatch(resetMatterDetails());
      dispatch(setDocListingPage(1));
      dispatch(updateShowActions(true));
      dispatch(setCurrentContext({ matterId: null, folderId: null }));
      // Clean up any lingering scrollbars and reset scroll state
      cleanupScrollState();
      
      // localStorage.removeItem("relativePath"); //TO UNCOMMENT ONCE STRICT MODE IS FIXED
    };
  }, [dispatch]);

  // Hide the file-tree sidebar automatically when the AI panel is opened
  // via URL/query param so the user gets the full document viewer + AI
  // panel side-by-side. (Both `?ai=…` and the legacy `?chatbox=true`
  // count as "open" via `isAIOpen`.)
  useEffect(() => {
    const justOpened = isAIOpen && !wasAIOpenRef.current;
    wasAIOpenRef.current = isAIOpen;
    if (justOpened && showSideBar) {
      autoHidingSidebarRef.current = true;
      setShowSideBar(false);
      localStorage.setItem("sidebar", "false");
    }
  }, [isAIOpen, showSideBar]);

  useEffect(() => claimBriefReaderMatterWindowName(matterId), [matterId]);

  // Reset hover-expand state whenever the sidebar is hidden so it doesn't
  // reopen pre-expanded (onMouseLeave never fires on unmount).
  useEffect(() => {
    if (!showSideBar) {
      autoHidingSidebarRef.current = false;
      if (isAIOpen && !isAIPaneResizing) {
        const nextWidth = clampAIPaneWidth(aiPaneWidth);
        if (aiPaneWidth !== nextWidth) {
          setAiPaneWidth(nextWidth);
          aiPaneWidthRef.current = nextWidth;
          localStorage.setItem("aiPaneWidth", nextWidth);
        }
      }
      setIsSidebarHovered(false);
    }
  }, [aiPaneWidth, clampAIPaneWidth, isAIOpen, isAIPaneResizing, showSideBar]);

  // Begin a resize drag. Captures cursor X + the currently-visible sidebar
  // width so handleMouseMove can apply movement as a delta (no jump). If
  // the sidebar is currently hover-expanded, commit that visible width
  // into sidebarWidth first so flipping isResizing doesn't collapse it
  // back to the compact width before the cursor has moved.
  const startResize = (e) => {
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const currentVisible = shouldHoverExpand ? effectiveSidebarWidth : sidebarWidth;
    dragStartRef.current = { startX: clientX, startWidth: currentVisible };
    if (shouldHoverExpand) {
      setSidebarWidth(effectiveSidebarWidth);
    }
    setIsResizing(true);
  };

  const startAIPaneResize = (e) => {
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    aiPaneDragStartRef.current = { startX: clientX, startWidth: aiPaneWidth };
    setIsAIPaneResizing(true);
  };

  // Gate the full-page shell skeleton on the two "no data yet" states:
  //   idle    → component mounted but fetch hasn't fired yet (brief)
  //   loading → fetch is in flight
  // On `succeeded` / `failed` we hide the skeleton so the real chrome
  // (which is always mounted) is visible — the shell skeleton is
  // specifically for "we expect data soon," not "we gave up."
  //
  // IMPORTANT: we render the skeleton as an absolutely-positioned overlay
  // ON TOP of the real chrome rather than *replacing* it via early-return.
  // The initial `fetchMatterDetail` bootstrap dispatch lives inside
  // `TabView` (a child of the real chrome), so early-returning during
  // `idle | loading` meant `TabView` never mounted, the fetch never
  // fired, `matterDetailsStatus` never left `"idle"`, and the skeleton
  // displayed forever — shipped as PR #60 and hotfixed here. If you're
  // tempted to move this back to an early-return, first hoist the
  // initial fetch out of `TabView`.
  const isInitialLoad =
    matterDetailsStatus === "idle" || matterDetailsStatus === "loading";

  // Two-stage skeleton lifecycle so the overlay can fade out cleanly
  // instead of popping. While `isInitialLoad` is true the overlay is
  // mounted AND visible; when it flips false we first flip visibility
  // off (triggering the CSS opacity transition on `ShellSkeletonOverlay`)
  // then unmount after the transition finishes. Re-entering loading (e.g.
  // navigating between matters) remounts immediately so the fade never
  // catches a half-loaded state.
  const [isSkeletonMounted, setIsSkeletonMounted] = useState(isInitialLoad);
  const [isSkeletonVisible, setIsSkeletonVisible] = useState(isInitialLoad);
  useEffect(() => {
    if (isInitialLoad) {
      setIsSkeletonMounted(true);
      setIsSkeletonVisible(true);
      return undefined;
    }
    setIsSkeletonVisible(false);
    // Keep in sync with the 180ms transition in ShellSkeletonOverlay.
    const timer = setTimeout(() => setIsSkeletonMounted(false), 200);
    return () => clearTimeout(timer);
  }, [isInitialLoad]);

  return (
    <MatterDetailsWrapper aiPaneOpen={isAIOpen}>
      {isSkeletonMounted && (
        <ShellSkeletonOverlay
          className={isSkeletonVisible ? "visible" : "hidden"}
          aria-hidden={!isSkeletonVisible}
        >
          <MatterDetailsShellSkeleton showSidebar={showSideBar} />
        </ShellSkeletonOverlay>
      )}
      {showSideBar && (
  <SideBarContainer
    onMouseEnter={() => setIsSidebarHovered(true)}
    onMouseLeave={() => setIsSidebarHovered(false)}
  >
    <SideBarWrapper width={`${effectiveSidebarWidth}px`} $animate={!isResizing}>
      <CollapseBtnWrapper>
        {/* Back to Matters button removed — navigation available via sidebar breadcrumb and browser back.
            The old inline spinner shown during `matterDetailsStatus === "loading"` was replaced by the
            full-page `MatterDetailsShellSkeleton` above — once we reach this render path, the matter
            has already loaded. */}
        <Popover placement="bottomLeft" content={"Collapse Folder Panel"}>
          <button
            onClick={() => {
              setShowSideBar(false);
              localStorage.setItem("sidebar", "false");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setShowSideBar(false);
                localStorage.setItem("sidebar", "false");
              }
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            aria-label="Collapse Sidebar"
          >
            <img src={CollapseIcon} alt="Collapse Sidebar" />
          </button>
        </Popover>
        <Button
          size="small"
          onClick={() => navigate(`/matter/${matterIdParam}/processing`)}
          aria-label="Open document processing page"
          data-testid="open-processing-page"
          title="View document processing status"
        >
          Processing
        </Button>
      </CollapseBtnWrapper>

      <TreeComponent
        treeData={newTreeData}
        breadcrumb={breadcrumb}
        hasNavigatedRef={hasNavigatedRef}
        setBreadcrumb={setBreadcrumb}
        setUniqueId={setUniqueId}
        checkTabLimit={(documentUniqueId) => checkTabLimitRef.current ? checkTabLimitRef.current(documentUniqueId) : false}
        openTab={openTabRef.current}
        setActiveTab={setActiveTabRef.current}
      />
    </SideBarWrapper>

    <ResizerButton
      onMouseDown={startResize}
      onTouchStart={startResize}
      aria-label="Resize sidebar"
      aria-orientation="vertical"
    />
  </SideBarContainer>
)}

      <MainContentWrapper aiPaneOpen={isAIOpen}>
        <TabView
          showSideBar={showSideBar}
          setShowSideBar={setShowSideBar}
          breadcrumb={breadcrumb}
          setBreadcrumb={setBreadcrumb}
          uniqueId={uniqueId}
          setUniqueId={setUniqueId}
          treeData={newTreeData}
          drawerError={drawerError}
          setdrawerError={setdrawerError}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          checkTabLimitRef={checkTabLimitRef}
          cleanupDocumentRef={cleanupDocumentRef}
          openTabRef={openTabRef}
          setActiveTabRef={setActiveTabRef}
          onTabClosed={(tabId, docId) => {
            clearFactSelectionRef.current?.(docId);
            // Drop cached briefing + jobs for the closed doc so the
            // next time it's reopened we re-fetch instead of showing
            // a stale empty/loading state from a previous visit.
            if (docId) dispatch(clearDocumentAIState(docId));
          }}
        />
      </MainContentWrapper>

      <AIPaneWrapper
        isOpen={isAIOpen}
        $width={`${aiPaneWidth}px`}
        aria-hidden={!isAIOpen}
      >
        {isAIOpen && (
          <>
            <AIPaneResizerButton
              onMouseDown={startAIPaneResize}
              onTouchStart={startAIPaneResize}
              aria-label="Resize AI panel"
              aria-orientation="vertical"
            />
            <AIPane
              mode={
                // VA CAVC matters get a chat-only panel: the matter is a
                // single RBA conversed with via chat; there's no VA
                // Agency graph behind "The Brief". The case type rides
                // on the matter-details payload already in the store —
                // but only trust it when the loaded tree belongs to THIS
                // matter: during cross-matter navigation Redux holds the
                // previous matter's tree until fetchMatterDetail lands,
                // which would misroute the panel (Bugbot 265/R1). Until
                // the right payload arrives we default to standard
                // matter mode; a brief tab-strip flash on a CAVC matter
                // beats being stuck chat-only on a VA Agency matter.
                String(matterDetailsTree?.id) === String(matterId) &&
                isChatOnlyCaseType(matterDetailsTree?.case_type)
                  ? AI_PANE_MODE_MATTER_CHAT_ONLY
                  : AI_PANE_MODE_MATTER
              }
              activeDocumentId={activeDocumentId}
              matterId={matterId}
              clearFactSelectionRef={clearFactSelectionRef}
              setActiveTabRef={setActiveTabRef}
            />
          </>
        )}
      </AIPaneWrapper>
    </MatterDetailsWrapper>
  );
};

export default MatterDetails;
